import { useCallback, useEffect, useRef, useState } from 'react'

import { attempt } from '~/api/attempt'
import { api } from '~/api/client'
import type { Found, Person } from '~/api/types'
import { Avatar } from './Avatar'
import { AppLink } from '~/components/AppLink'
import { SEARCH_DEBOUNCE_MS } from '~/components/Search'
import { onePress } from '~/routes/writes'

import s from './Friends.module.css'

/**
 * Adding and managing the people you go out with.
 *
 * This existed and then did not. The Python page rendered it into the document and baked
 * the list into the payload, so when the app replaced that page the whole interface went
 * with it — no endpoint, no component, no way to accept a request somebody had sent you.
 *
 * **The three verbs are the whole design.** A connection is not a boolean: it is a status
 * and a direction, and each pair means something different to the person looking at it.
 *
 *   accepted            → Remove
 *   pending, they asked → Accept  (and Decline)
 *   pending, you asked  → Cancel
 *
 * Offering "Add" to somebody who has already asked you is the mistake this shape exists to
 * prevent — it reads as though your request went nowhere, and it makes a second row rather
 * than settling the first.
 */

/** What a row can offer. Named, because the mapping from state to verb is the thing. */
type Verb = 'accept' | 'decline' | 'cancel' | 'remove' | 'add'

/**
 * Removing a friend is behind a step, and the others are not.
 *
 * Accept, Decline and Cancel are why somebody opened this page: there is a request in front
 * of them and the page exists so they can answer it. Remove is not — people fall out about
 * twice a decade, and a button that ends a friendship should not sit at the same weight as
 * the one that starts it, one mis-tap from a list you scroll past to find somebody.
 *
 * So an accepted row offers nothing at rest. The control that reveals Remove is quiet, has
 * to be pressed on purpose, and puts the name in the question — "Remove Sam?" rather than
 * "Are you sure?", because a confirmation that does not say what it is confirming is a
 * second button rather than a second thought.
 */
function verbsFor(person: Person): Verb[] {
  if (person.status === 'accepted') return []
  return person.direction === 'in' ? ['accept', 'decline'] : ['cancel']
}

/** The same question about a search result, which may have no connection at all. */
function verbsForFound(found: Found): Verb[] {
  // A search result that is already a friend says so and offers nothing: you did not search
  // for somebody in order to un-friend them.
  if (found.status === 'accepted') return []
  if (found.status === 'pending') return found.direction === 'in' ? ['accept', 'decline'] : ['cancel']
  return ['add']
}

const LABEL: Record<Verb, string> = {
  accept: 'Accept',
  decline: 'Decline',
  cancel: 'Cancel',
  remove: 'Remove',
  add: 'Add',
}

/** A connection as a row draws it. `null` is no connection at all. */
type Link = { status: 'pending' | 'accepted'; direction: 'in' | 'out' } | null

/**
 * What a press is expected to leave behind, for drawing only. The server decides what the
 * press means (`befriend` asks or accepts, `remove_friend` declines, cancels or removes);
 * this is the client's guess at the row while the request is out, and the answer replaces it.
 */
function after(verb: Verb, was: Link): Link {
  if (verb === 'accept') return { status: 'accepted', direction: was?.direction ?? 'in' }
  if (verb === 'add') return { status: 'pending', direction: 'out' }
  return null
}

/** Who a press was made on: enough to draw a row for somebody not yet in the list. */
type Who = { accountId: number; username: string; name: string }

/** A press that has not been answered yet. Compared by identity, so two on one person stay two. */
interface Press {
  verb: Verb
  who: Who
}

/** The list with one press drawn on top, through `after`, so the list and a search row agree. */
function pressOn(list: Person[], press: Press): Person[] {
  const at = list.findIndex(f => f.accountId === press.who.accountId)
  const was = at < 0 ? null : list[at]!
  const next = after(press.verb, was)
  if (next === null) return at < 0 ? list : list.filter((_, i) => i !== at)
  // Spread over the row already there, so a card drawn for an Accept keeps its face.
  const row: Person = was
    ? { ...was, ...next }
    : { ...press.who, ...next, hasAvatar: false }
  return at < 0 ? [...list, row] : list.map((f, i) => (i === at ? row : f))
}

/**
 * When a read was sent: how many write answers had landed. A read that lands after a later
 * answer may describe the server before that write, so it must not be drawn over the
 * answer's list.
 *
 * A write that was merely out when the read was sent does not make the read stale. Every
 * write changes only the viewer's link to the one person it names, and every press still out
 * is drawn on top of whatever the row was read from. For that person the press decides; for
 * everybody else the read was sent after the last answer had landed, so no answered write is
 * missing from it. Falling back to the last answer there drew a list older than the read, and
 * offered Add to somebody whose request the read had just reported.
 */
export interface Stamp {
  answers: number
}

export function useFriends() {
  // The last whole list the server sent, and how many write answers it has seen; together
  // because a read is judged against the answer count at the moment it lands.
  const [base, setBase] = useState<{ list: Person[]; answers: number }>({ list: [], answers: 0 })
  const [out, setOut] = useState<Press[]>([])
  const [failed, setFailed] = useState<string | null>(null)

  // A mirror of the answer count for `stamp`, which runs inside timers and promise callbacks
  // where the rendered value would be the one from when the closure was made.
  const answers = useRef(0)
  // Writes pressed and not yet finished, so a press knows whether one is ahead of it.
  const pending = useRef(0)
  const stamp = useCallback((): Stamp => ({ answers: answers.current }), [])

  /*
   * One write at a time, in press order. Every answer is the whole list, so two writes in
   * flight could be answered out of order and the older list would undo the newer press;
   * queued, the last answer is always the truth. A refusal resolves rather than rejects
   * (`attempt` answers false), so one refused write does not stop the ones queued behind it.
   */
  const queue = useRef<Promise<void>>(Promise.resolve())

  const load = useCallback(() => {
    const asked = stamp()
    api
      .friends()
      .then(answer => {
        // `?? []` because a panel must not be able to take down the page it sits on. This
        // sits inside the profile, and an answer without a `friends` key — a stale server, a
        // proxy returning something else — threw on `.filter` and unmounted the whole route.
        const list = answer.friends ?? []
        // A load is a read like a search: older than a write answer, it is dropped.
        setBase(was => (was.answers === asked.answers ? { list, answers: was.answers } : was))
      })
      .catch((error: unknown) => setFailed(error instanceof Error ? error.message : String(error)))
  }, [stamp])

  useEffect(load, [load])

  /**
   * Every action answers with the whole list, so it is taken rather than patched.
   *
   * Accepting somebody moves them between sections and can settle a request in the other
   * direction at the same time; a client patching the one row it pressed would be guessing
   * at the rest of the list. The press is drawn at once (`drawn` below) only while it is out:
   * the answer's list replaces it, and a refusal simply drops it, which puts the row back.
   */
  const act = useCallback((verb: Verb, who: Who) => {
    const press: Press = { verb, who }
    pending.current += 1
    setOut(was => [...was, press])
    const send = async () => {
      const got: { list?: Person[] } = {}
      // Through `attempt`, so a refusal is said in the site's one sentence for a failed write,
      // a 401 goes to sign in, and the next success clears the sentence.
      await attempt(async () => {
        const answer =
          verb === 'accept' || verb === 'add'
            ? await api.befriend(who.accountId)
            : await api.unfriend(who.accountId)
        got.list = answer.friends ?? []
      })
      pending.current -= 1
      const taken = got.list
      if (taken) {
        answers.current += 1
        setBase(was => ({ list: taken, answers: was.answers + 1 }))
      }
      setOut(was => was.filter(p => p !== press))
    }
    // With nothing ahead of it the write starts in the press itself rather than a tick later.
    // `pending` is lowered with no `await` between it and the end of `send`, so a count of
    // one here means every earlier write has finished, not merely been answered.
    queue.current = pending.current === 1 ? send() : queue.current.then(send)
  }, [])

  const drawn = out.reduce(pressOn, base.list)

  return { friends: drawn, base: base.list, answers: base.answers, out, failed, act, stamp, reload: load }
}

/**
 * A search row's connection.
 *
 * `/people` reads the link from the same `friends_of` join the list comes from, so either can
 * say it. The search answer is used when no write answer has landed since it was sent (see
 * `Stamp` for why a write still out does not count); otherwise the last answer's list is
 * newer, and the row is read from it by account id — no entry, no connection. Either way the
 * presses still out are drawn on top, through the same `after` the list uses.
 */
function linkFor(found: Found, from: Stamp, answers: number, base: Person[], out: Press[]): Link {
  let link: Link
  if (from.answers === answers) {
    link = found.status && found.direction ? { status: found.status, direction: found.direction } : null
  } else {
    const row = base.find(f => f.accountId === found.id)
    link = row ? { status: row.status, direction: row.direction } : null
  }
  for (const press of out) if (press.who.accountId === found.id) link = after(press.verb, link)
  return link
}

/** Requests waiting on you. The count the masthead badges. */
export function waitingOnYou(friends: Person[] | undefined): Person[] {
  return (friends ?? []).filter(f => f.status === 'pending' && f.direction === 'in')
}

/** An accepted friend: a face, a name, a handle — and the quiet way to end it. */
function Card({ person, onAct }: { person: Person; onAct: (verb: Verb) => void }) {
  const [asking, setAsking] = useState(false)

  if (asking) {
    return (
      <li className={s.card}>
        <span className={s.asking}>
          Remove {person.name}?
          <span className={s.acts}>
            {/* Manage swaps the card for this question at the first click, so the second click
                of a double click on Manage can land on Remove. */}
            <button type="button" className={s.warn} {...onePress(() => onAct('remove'))}>
              Remove
            </button>
            <button type="button" className={s.no} onClick={() => setAsking(false)}>
              Keep
            </button>
          </span>
        </span>
      </li>
    )
  }

  return (
    <li className={s.card}>
      <Avatar
        accountId={person.accountId}
        name={person.name}
        has={person.hasAvatar}
        size="small"
      />
      <AppLink className={s.who ?? ''} to={`/u/${person.username}`}>
        <b className={s.fname}>{person.name}</b>
        <small className={s.fhandle}>@{person.username}</small>
      </AppLink>
      <span className={s.acts}>
        <button
          type="button"
          className={s.quiet}
          aria-label={`Manage ${person.name}`}
          onClick={() => setAsking(true)}
        >
          &#8943;
        </button>
      </span>
    </li>
  )
}

/** A request, in or out. A row rather than a card: it is a decision, not a person. */
function Ask({
  person,
  said,
  verbs,
  onAct,
}: {
  person: { accountId?: number; id?: number; username: string; name: string }
  said: string
  verbs: Verb[]
  onAct: (verb: Verb) => void
}) {
  return (
    <div className={s.ask}>
      <AppLink className={s.aname ?? ''} to={`/u/${person.username}`}>
        {person.name}
      </AppLink>
      <span className={s.awhen}>{said}</span>
      {/* A press moves its row at once, so the second click of a double click can land on
          the next row's button, or on the button that replaced this one in the same row. */}
      <span className={s.acts}>
        {verbs.map(verb => (
          <button
            key={verb}
            type="button"
            className={verb === 'accept' || verb === 'add' ? s.yes : s.no}
            {...onePress(() => onAct(verb))}
          >
            {LABEL[verb]}
          </button>
        ))}
      </span>
    </div>
  )
}

export function Friends() {
  const { friends, base, answers, out, failed, act, stamp } = useFriends()
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<{ rows: Found[]; from: Stamp } | null>(null)
  const timer = useRef<number | undefined>(undefined)

  /*
   * Debounced, and an emptied box clears rather than searching. The server answers an empty
   * query with an empty list, so this is not about correctness — it is that a cleared box
   * should show your friends again rather than nothing at all.
   *
   * Live until the query moves on, as in the site's search: without it a slow answer for
   * `ze` landing after the answer for `zed` replaces the right list with the wrong one.
   */
  useEffect(() => {
    window.clearTimeout(timer.current)
    const text = query.trim()
    if (!text) {
      setFound(null)
      return
    }
    let live = true
    timer.current = window.setTimeout(() => {
      const from = stamp()
      const take = (rows: Found[]) => {
        if (live) setFound({ rows, from })
      }
      void api.people(text).then(take).catch(() => take([]))
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      live = false
      window.clearTimeout(timer.current)
    }
  }, [query, stamp])

  const waiting = waitingOnYou(friends)
  const settled = friends.filter(f => f.status === 'accepted')
  const asked = friends.filter(f => f.status === 'pending' && f.direction === 'out')

  return (
    <section className={s.wrap} aria-label="Friends">
      <label className={s.find}>
        <span className="visually-hidden">Find someone</span>
        <input
          type="search"
          value={query}
          placeholder="Find someone by name"
          autoComplete="off"
          onChange={e => setQuery(e.target.value)}
        />
      </label>

      {failed ? <p className={s.trouble}>{failed}</p> : null}

      {found !== null ? (
        found.rows.length === 0 ? (
          <p className={s.none}>Nobody by that name.</p>
        ) : (
          <div className={s.asked}>
            <h3>Results</h3>
            {found.rows.map(hit => {
              const link = linkFor(hit, found.from, answers, base, out)
              const person: Found = { ...hit, status: link?.status ?? null, direction: link?.direction ?? null }
              return (
                <Ask
                  key={person.id}
                  person={person}
                  said={person.status === 'accepted' ? 'already a friend' : `@${person.username}`}
                  verbs={verbsForFound(person)}
                  onAct={verb => act(verb, { accountId: hit.id, username: hit.username, name: hit.name })}
                />
              )
            })}
          </div>
        )
      ) : (
        <>
          {settled.length === 0 ? (
            <p className={s.none}>Nobody yet. Search above to add someone.</p>
          ) : (
            <ul className={s.folk}>
              {settled.map(person => (
                <Card
                  key={person.accountId}
                  person={person}
                  onAct={verb => act(verb, person)}
                />
              ))}
            </ul>
          )}

          {/* Waiting on you: the one lit thing in this panel, because it is the only part
              of it that is somebody else waiting for an answer. */}
          {waiting.length > 0 ? (
            <div className={s.asked}>
              <h3>
                Waiting on you · {waiting.length}
              </h3>
              {waiting.map(person => (
                <Ask
                  key={person.accountId}
                  person={person}
                  said="wants to be friends"
                  verbs={verbsFor(person)}
                  onAct={verb => act(verb, person)}
                />
              ))}
            </div>
          ) : null}

          {asked.length > 0 ? (
            <div className={`${s.asked} ${s.sent}`}>
              <h3>You asked</h3>
              {asked.map(person => (
                <Ask
                  key={person.accountId}
                  person={person}
                  said="waiting on them"
                  verbs={verbsFor(person)}
                  onAct={verb => act(verb, person)}
                />
              ))}
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}
